<template>
  <div class="content-wrapper relative max-w-full text-headline">
    <div
      ref="contentRef"
      data-cy="activity-content"
      :class="{
        'content-clamped': shouldApplyHiddenState,
        'min-h-4': isThread,
      }"
    >
      <markdown-text :content class="whitespace-pre-wrap break-words" />
    </div>
    <div v-if="shouldApplyHiddenState && !isThread" class="button-container">
      <button
        class="change-state-button"
        @click.stop.prevent="isExpanded = true"
      >
        {{ $t("COMMON.SHOW_MORE") }}
      </button>
    </div>
    <div
      v-else-if="isExpanded && hasOverflow"
      ref="showLessAnchorRef"
      data-cy="inline-show-less"
      class="show-less-container"
    >
      <button
        class="change-state-button font-medium focus:outline-none"
        @click.stop.prevent="isExpanded = false"
      >
        {{ $t("COMMON.SHOW_LESS") }}
      </button>
    </div>
    <div
      v-else-if="isThread"
      class="absolute flex bottom-px items-center right-0 max-w-26"
    >
      <button
        class="change-state-button"
        @click.stop.prevent="emit('thread:open')"
      >
        {{ $t("COMMON.SHOW_THREAD") }}
      </button>
    </div>
    <teleport v-if="teleportTargetRef" :to="`#${TELEPORT_TARGET_ID}`">
      <div
        v-if="shouldShowFloatingButton"
        data-cy="floating-show-less"
        class="absolute bottom-3 left-4 z-10 flex w-fit"
      >
        <button
          class="rounded-lg border bg-white px-4 py-2 font-medium whitespace-nowrap text-primary shadow-lg focus:outline-none"
          @click.stop.prevent="isExpanded = false"
        >
          {{ $t("COMMON.SHOW_LESS") }}
        </button>
      </div>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { useElementVisibility, useResizeObserver } from "@vueuse/core";
import { computed, nextTick, onMounted, ref, watch } from "vue";

import MarkdownText from "@/components/ui/text/MarkdownText.vue";

const { content, isThread = false } = defineProps<{
  content: string;
  isThread?: boolean;
}>();

const emit = defineEmits<{
  "thread:open": [];
}>();

const TELEPORT_TARGET_ID = "deal-activity-main-body";

const contentRef = ref<HTMLElement>();
const showLessAnchorRef = ref<HTMLElement>();
const teleportTargetRef = ref<HTMLElement | null>(null);
const isExpanded = ref(false);
const hasOverflow = ref(false);

// Track visibility using useElementVisibility (simpler and auto-cleans up)
const isAnchorVisible = useElementVisibility(showLessAnchorRef);
const isContentInView = useElementVisibility(contentRef);

const shouldShowFloatingButton = computed(() => {
  if (!isExpanded.value || !hasOverflow.value || isAnchorVisible.value) {
    return false;
  }
  return isContentInView.value;
});

const checkOverflow = async () => {
  await nextTick();

  if (!contentRef.value) {
    return;
  }

  const hadLineClamp = contentRef.value.classList.contains("content-clamped");

  if (!hadLineClamp) {
    contentRef.value.classList.add("content-clamped");
  }

  await nextTick();

  // Compare scroll height vs client height WITH line-clamp applied
  // scrollHeight = full content height, clientHeight = visible 2 lines
  hasOverflow.value =
    contentRef.value.scrollHeight > contentRef.value.clientHeight;

  if (!hadLineClamp) {
    contentRef.value.classList.remove("content-clamped");
  }
};

const shouldApplyHiddenState = computed(
  () => hasOverflow.value && !isExpanded.value
);

// Check on mount and when content changes
onMounted(() => {
  teleportTargetRef.value = document.getElementById(TELEPORT_TARGET_ID);
  checkOverflow();
});
watch(() => content, checkOverflow);

// Re-check on container resize
useResizeObserver(contentRef, checkOverflow);
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.content-wrapper {
  @apply relative;
}

.content-clamped {
  @apply overflow-hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  word-break: break-word;
  padding-right: 5rem;
}

.button-container {
  @apply absolute flex items-center;
  bottom: 1px;
  right: 0;
  max-width: 5rem;
}

.change-state-button {
  @apply font-medium whitespace-nowrap text-primary focus:outline-none;
}

.show-less-container {
  @apply mt-2 flex justify-start;
}
</style>
